import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch } from "@/components/ui";
import { actionsKeys, FIXED_WEIGHTS, permissionActions } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDistributionRuleByCommunityYear, useUpdateRuleStatusMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, Eye, MoreVertical } from "lucide-react";
import moment from "moment";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { RULE_ID } from "../../consts";

const useFixedWeight = () => {
  const { level, type, action, glId } = useParams();
  const queryClient = useQueryClient();
  const [openConfigureDialog, setOpenConfigureDialog] = useState<boolean>(false);
  const configuredData = useRef<any>(null);
  const isView = action == "view";
  const location = useLocation();
  const fromTraceability = location?.pathname?.includes(routes.traceability);

  const { communityData, currentFinanceYear, accessPermission } = useSelector(
    (state: RootState) => state.currentData
  );
  const communityName = communityData?.communityName || communityData?.name || "";
  
  const hasCreatePermission = useMemo(() => accessPermission?.[actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase()]?.[permissionActions.CREATE], [accessPermission])
  const hasUpdatePermission = useMemo(() => accessPermission?.[actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase()]?.[permissionActions.UPDATE], [accessPermission])
  
  const orgBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionRuleConfiguration}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: "",
        name: <span className="text-primary">Configure Fixed Weight</span>,
      },
    ],
    [communityName]
  );

  const traceabilityBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}`,
        name: `Traceability`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}/${routes.distributionRuleConfiguration}/${glId}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: "",
        name: <span className="text-primary">Configure Fixed Weight</span>,
      },
    ],
    [communityName]
  );

  const breadcrumbData = fromTraceability ? traceabilityBreadcrumbData : orgBreadcrumbData;

  const {data: communityRules, isLoading: isLoadingRules } = useGetDistributionRuleByCommunityYear({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  });

  const tableData = useMemo(() => {
    return communityRules?.data?.filter((data: any) => data?.distributionMethod == FIXED_WEIGHTS && data?.budgetType == type && data?.level == level);
  }, [communityRules])

  const { mutate: updateRuleStatus, isPending: isUpdateStatusPending } =
    useUpdateRuleStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`Status Updated Successfully`);
      },
    });
    
  const columns = [
    {
      accessorKey: "ruleName",
      header: <div>Rule Name</div>,
    },
    {
      accessorKey: "createdAt",
      header: <div>Uploaded On</div>,
      accessorFn: (row: any) => row?.createdAt ? moment(row?.createdAt).format("MM/DD/YYYY") : ""
    },
    {
      accessorKey: "active",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        return (
          <Switch
            id="active"
            onCheckedChange={() => {
              updateRuleStatus({ruleId: row?.original?.[RULE_ID],  communityId: communityData?.communityId})
            }}
            checked={row?.original?.active}
            disabled={isUpdateStatusPending || isView}
          />
        )
      }
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({row}: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  configuredData.current = {...row?.original, key: FIXED_WEIGHTS, action: "view"};
                  setOpenConfigureDialog(true);
                }}
              >
                <Eye className="w-4 h-4" />
                View
              </DropdownMenuItem>
              {isView || !hasUpdatePermission ? <></> : <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  configuredData.current = {...row?.original, key: FIXED_WEIGHTS, action: "edit"};
                  setOpenConfigureDialog(true)
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onAdd = () => {
    configuredData.current = {key: FIXED_WEIGHTS, action: "add"};
    setOpenConfigureDialog(true);
  };

  return {
    breadcrumbData,
    currentFinanceYear,
    isLoadingRules,
    tableData,
    columns,
    level,
    onAdd,
    openConfigureDialog,
    setOpenConfigureDialog,
    type,
    configuredData: configuredData.current,
    isView,
    hasCreatePermission
  }
}

export default useFixedWeight;